import {
  MENU_TIER_LOCK_HREF_TO_KEY,
  MENU_TIER_LOCK_REGISTRY,
  planDisplayName,
  type AccountType,
} from "@shared/schema";

/**
 * Registry key → human label (e.g. `operations.tasks` → "Tasks"), derived once
 * from `MENU_TIER_LOCK_REGISTRY`. Shared by the global `UpgradeRequiredListener`
 * and the per-page `InlineUpgradePanel` so both name a locked section the same
 * way.
 */
export const MENU_TIER_LOCK_KEY_TO_LABEL: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  for (const group of MENU_TIER_LOCK_REGISTRY) {
    for (const item of group.items) {
      map[item.key] = item.label;
    }
  }
  return map;
})();

/**
 * Resolve a client route `path` to the menu-tier-lock section it belongs to,
 * via a longest-prefix match over `MENU_TIER_LOCK_HREF_TO_KEY` (so a sub-route
 * like `/app/financials/income` resolves to the most specific registered
 * section). Returns `{ key, href }` for the matched section, or `null` for
 * routes that are not part of any tier-lockable section.
 *
 * Purely path-based — it does NOT depend on the user's denied tiers — so it is
 * valid even before `/api/app/menu-tier-locks/denied` has loaded. The
 * route-level overlay (`routeTierLock` in app-layout) layers the denied/tier
 * checks on top of this.
 */
export function resolveRouteMenuLock(
  path: string,
): { key: string; href: string } | null {
  let p = path.split("?")[0].split("#")[0];
  if (p.length > 1 && p.endsWith("/")) p = p.slice(0, -1);
  let bestHref: string | null = null;
  for (const href of Object.keys(MENU_TIER_LOCK_HREF_TO_KEY)) {
    // Registry hrefs carrying a hash (e.g. /app/dashboard#pulse) never match a
    // real wouter location, so skip them.
    if (href.includes("#")) continue;
    if (p === href || p.startsWith(href + "/")) {
      if (bestHref === null || href.length > bestHref.length) bestHref = href;
    }
  }
  if (bestHref === null) return null;
  return { key: MENU_TIER_LOCK_HREF_TO_KEY[bestHref], href: bestHref };
}

/**
 * The menu-tier-lock registry key for the section a client route belongs to, or
 * `null`. Used by the global `UpgradeRequiredListener` to fire its modal ONLY
 * when a 403 `UPGRADE_REQUIRED` names the section the user is currently viewing
 * — a background/secondary query that 403s for a DIFFERENT (locked) section
 * while the user sits on an allowed page (dashboard, properties, …) must never
 * pop the upgrade modal.
 */
export function routeMenuLockKey(path: string): string | null {
  return resolveRouteMenuLock(path)?.key ?? null;
}

/**
 * Resolve the upgrade copy for a locked menu-tier-lock `key` using the
 * `lockedTiers` map returned by `/api/app/menu-tier-locks/denied` (the same
 * source the sidebar uses, so the two can't disagree). Falls back to a generic
 * label/tier when the key is unknown or its tier hasn't loaded yet.
 */
export function resolveUpgradeInfo(
  key: string | null,
  lockedTiers: Record<string, AccountType> | undefined,
): { label: string; requiredTier: AccountType; reason: string } {
  const requiredTier: AccountType =
    (key ? lockedTiers?.[key] : undefined) ?? "Professional";
  const label =
    (key ? MENU_TIER_LOCK_KEY_TO_LABEL[key] : undefined) ?? "This section";
  const reason = `${label} requires the ${planDisplayName(requiredTier)} plan.`;
  return { label, requiredTier, reason };
}
